import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import './Admin.css';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: ''
  });
  const [isFixingGrammar, setIsFixingGrammar] = useState(false);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [titleSuggestion, setTitleSuggestion] = useState('');
  const [descriptionSuggestion, setDescriptionSuggestion] = useState('');

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setIsAuthenticated(true);
      }
    };
    
    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setIsAuthenticated(true);
        } else if (event === 'SIGNED_OUT') {
          setIsAuthenticated(false);
          setUsername('');
          setPassword('');
          setEditingPost(null);
          setFormData({
            title: '',
            excerpt: '',
            content: '',
            author: ''
          });
        }
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username,
        password: password
      });

      if (error) throw error;
      
      setIsAuthenticated(true);
      setError('');
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid credentials');
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setIsAuthenticated(false);
      setUsername('');
      setPassword('');
      setEditingPost(null);
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        author: ''
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }

    try {
      if (editingPost) {
        // Update existing post
        const { error } = await supabase
          .from('blog_posts')
          .update({
            title: formData.title,
            excerpt: formData.excerpt,
            content: formData.content,
            author: formData.author,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingPost.id);

        if (error) throw error;
      } else {
        // Create new post
        const { error } = await supabase
          .from('blog_posts')
          .insert([{
            title: formData.title,
            excerpt: formData.excerpt,
            content: formData.content,
            author: formData.author,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (error) throw error;
      }

      // Reset form and refresh posts
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        author: ''
      });
      setEditingPost(null);
      fetchPosts();
      setError('');
    } catch (error) {
      console.error('Error saving post:', error);
      setError('Error saving post. Please try again.');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt || '',
      content: post.content,
      author: post.author || ''
    });
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        const { error } = await supabase
          .from('blog_posts')
          .delete()
          .eq('id', postId);

        if (error) throw error;
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
        setError('Error deleting post. Please try again.');
      }
    }
  };

  const cancelEdit = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: ''
    });
  };

  // AI-powered grammar correction
  const handleFixGrammar = async () => {
    if (!formData.content.trim()) {
      setError('Please add some content to improve grammar, structure, and punctuation.');
      return;
    }

    setIsFixingGrammar(true);
    setError('');
    
    try {
      // Check if API key exists
      if (!process.env.REACT_APP_GEMINI_API_KEY || process.env.REACT_APP_GEMINI_API_KEY === 'your_gemini_api_key_here') {
        setError('No valid API key for text improvement.');
        return;
      }

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Fix the grammar, structure, and punctuation in this text. Improve sentence flow, fix any awkward phrasing, and ensure proper punctuation throughout. Return ONLY the corrected version of the text, nothing else. No markdown, no JSON, just the corrected text.

Text to fix:
${formData.content}`
            }]
          }]
        })
      });

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts[0]) {
        const correctedText = data.candidates[0].content.parts[0].text.trim();
        
        // Remove any markdown formatting that might be present
        const cleanText = correctedText
          .replace(/```json\s*/g, '')
          .replace(/```\s*/g, '')
          .replace(/^\[|\]$/g, '')
          .trim();
        
        // Update the content directly
        setFormData(prev => ({
          ...prev,
          content: cleanText
        }));
        
        setError('Grammar, structure, and punctuation improved successfully!');
      } else {
        throw new Error('Invalid AI response format');
      }
    } catch (error) {
      console.error('Error improving text:', error);
      setError('Error improving text. Please try again.');
    } finally {
      setIsFixingGrammar(false);
    }
  };

  // AI-powered title suggestion
  const handleSuggestTitle = async () => {
    console.log('formData:', formData);
    console.log('formData.content:', formData.content);
    
    if (!formData.content || !formData.content.trim()) {
      setError('Please add some content to generate a title suggestion.');
      return;
    }

    setIsGeneratingTitle(true);
    setError('');
    
    try {
      if (!process.env.REACT_APP_GEMINI_API_KEY || process.env.REACT_APP_GEMINI_API_KEY === 'your_gemini_api_key_here') {
        setError('No valid API key for title suggestions.');
        return;
      }

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Based on this blog post content, suggest a compelling and SEO-friendly title. Return ONLY the title, nothing else. No quotes, no markdown, just the title text.

Blog content:
${formData.content.substring(0, 1000)}`
            }]
          }]
        })
      });

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts[0]) {
        const suggestedTitle = data.candidates[0].content.parts[0].text.trim();
        setTitleSuggestion(suggestedTitle);
      } else {
        throw new Error('Invalid AI response format');
      }
    } catch (error) {
      console.error('Error generating title suggestion:', error);
      setError('Error generating title suggestion. Please try again.');
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  // AI-powered description suggestion
  const handleSuggestDescription = async () => {
    console.log('formData:', formData);
    console.log('formData.content:', formData.content);
    
    if (!formData.content || !formData.content.trim()) {
      setError('Please add some content to generate a description suggestion.');
      return;
    }

    setIsGeneratingDescription(true);
    setError('');
    
    try {
      if (!process.env.REACT_APP_GEMINI_API_KEY || process.env.REACT_APP_GEMINI_API_KEY === 'your_gemini_api_key_here') {
        setError('No valid API key for description suggestions.');
        return;
      }

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Based on this blog post content, suggest a compelling excerpt/description (2-3 sentences). Return ONLY the description, nothing else. No quotes, no markdown, just the description text.

Blog content:
${formData.content.substring(0, 1000)}`
            }]
          }]
        })
      });

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts[0]) {
        const suggestedDescription = data.candidates[0].content.parts[0].text.trim();
        setDescriptionSuggestion(suggestedDescription);
      } else {
        throw new Error('Invalid AI response format');
      }
    } catch (error) {
      console.error('Error generating description suggestion:', error);
      setError('Error generating description suggestion. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Apply title suggestion
  const applyTitleSuggestion = () => {
    if (titleSuggestion) {
      setFormData(prev => ({
        ...prev,
        title: titleSuggestion
      }));
      setTitleSuggestion('');
    }
  };

  // Apply description suggestion
  const applyDescriptionSuggestion = () => {
    if (descriptionSuggestion) {
      setFormData(prev => ({
        ...prev,
        excerpt: descriptionSuggestion
      }));
      setDescriptionSuggestion('');
    }
  };





  if (!isAuthenticated) {
    return (
      <div className="main-content">
        <section className="hero-section">
          <div className="hero-background">
            <div className="hero-gradient"></div>
            <div className="hero-grid-pattern"></div>
            <div className="hero-orbs">
              <div className="hero-orb hero-orb-1"></div>
              <div className="hero-orb hero-orb-2"></div>
              <div className="hero-orb hero-orb-3"></div>
            </div>
          </div>
          <div className="hero-content">
            <h1 className="hero-title">Admin Login</h1>
            <p className="hero-description">Access the blog management system</p>
          </div>
        </section>

        <section className="section">
          <div className="floating-stars">
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
            <div className="star"></div>
          </div>
          <div className="container">
            <div className="login-form">
              <h2>Prove It</h2>
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label htmlFor="username">Email</label>
                  <input
                    type="email"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    placeholder="Enter your email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                {error && <div className="error-message">{error}</div>}
                <button type="submit" className="login-btn">Login</button>
              </form>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="main-content">
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid-pattern"></div>
          <div className="hero-orbs">
            <div className="hero-orb hero-orb-1"></div>
            <div className="hero-orb hero-orb-2"></div>
            <div className="hero-orb hero-orb-3"></div>
          </div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">Blog Admin</h1>
          <p className="hero-description">Manage your blog posts and content</p>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <div className="admin-content">
            <div className="form-section">
              <h2>{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
              <form onSubmit={handleSubmit} className="post-form">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <div className="input-with-suggestion">
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter post title"
                    />
                    {!formData.title && formData.content && formData.content.trim() && (
                      <button
                        type="button"
                        className="suggestion-btn"
                        onClick={handleSuggestTitle}
                        disabled={isGeneratingTitle}
                      >
                        {isGeneratingTitle ? 'Generating...' : 'Suggest Title'}
                      </button>
                    )}
                    {!formData.title && (!formData.content || !formData.content.trim()) && (
                      <span className="suggestion-hint">Write some content first to get title suggestions</span>
                    )}
                  </div>
                  {titleSuggestion && (
                    <div className="suggestion-display">
                      <span className="suggestion-text">{titleSuggestion}</span>
                      <button
                        type="button"
                        className="apply-suggestion-btn"
                        onClick={applyTitleSuggestion}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="excerpt">Excerpt</label>
                  <div className="input-with-suggestion">
                    <textarea
                      id="excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      placeholder="Brief description of the post (optional)"
                      rows="3"
                    />
                    {!formData.excerpt && formData.content && formData.content.trim() && (
                      <button
                        type="button"
                        className="suggestion-btn"
                        onClick={handleSuggestDescription}
                        disabled={isGeneratingDescription}
                      >
                        {isGeneratingDescription ? 'Generating...' : 'Suggest Description'}
                      </button>
                    )}
                    {!formData.excerpt && (!formData.content || !formData.content.trim()) && (
                      <span className="suggestion-hint">Write some content first to get description suggestions</span>
                    )}
                  </div>
                  {descriptionSuggestion && (
                    <div className="suggestion-display">
                      <span className="suggestion-text">{descriptionSuggestion}</span>
                      <button
                        type="button"
                        className="apply-suggestion-btn"
                        onClick={applyDescriptionSuggestion}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="author">Author</label>
                  <input
                    type="text"
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Author name (optional)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="content">Content *</label>
                  <div className="content-editor-container">
                    <textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      required
                      placeholder="Write your blog post content here. You can use Markdown formatting."
                      rows="15"
                    />
                    <div className="ai-tools">
                      <button 
                        type="button" 
                        className="ai-btn fix-grammar-btn"
                        onClick={handleFixGrammar}
                        disabled={isFixingGrammar}
                      >
                        {isFixingGrammar ? 'Fixing...' : 'Fix Grammar'}
                      </button>

                    </div>
                  </div>
                  
                  {/* Simple Success Message */}
                  {error && error.includes('successfully') && (
                    <div className="success-message">
                      {error}
                    </div>
                  )}
                </div>

                {/* Error Message - Only show if it's not a success message */}
                {error && !error.includes('successfully') && <div className="error-message">{error}</div>}

                <div className="form-actions">
                  <button type="submit" className="submit-btn">
                    {editingPost ? 'Update Post' : 'Create Post'}
                  </button>
                  {editingPost && (
                    <button type="button" onClick={cancelEdit} className="cancel-btn">
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="posts-section">
              <h2>Existing Posts</h2>
              {posts.length === 0 ? (
                <p className="no-posts">No posts yet.</p>
              ) : (
                <div className="posts-list">
                  {posts.map((post) => (
                    <div key={post.id} className="post-item">
                      <div className="post-info">
                        <h3>{post.title}</h3>
                        <p className="post-meta">
                          Created: {new Date(post.created_at).toLocaleDateString()}
                          {post.author && ` | Author: ${post.author}`}
                        </p>
                        {post.excerpt && (
                          <p className="post-excerpt">{post.excerpt}</p>
                        )}
                      </div>
                      <div className="post-actions">
                        <button
                          onClick={() => handleEdit(post)}
                          className="edit-btn"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admin;
